import { LoadingScreen } from '@/shared/components'
import {
  useDownloadCompletedFinalizedReport,
  useDownloadFinalizedReportPreview,
  useGetCompletedFinalizedReportDownloadUrl,
  useGetFinalizedReportPreview
} from '@/shared/hooks/api/finalizedReports'
import { BreadcrumbsBar, Button, Spinner, generatePeriodId } from '@/common'
import {
  CheckCircleIcon,
  DownloadArrowDownIcon,
  PencilIcon
} from '@/common/components/icons'
import { Navigate, useParams } from 'react-router-dom'
import { useSession } from '@/shared/hooks/useSession'
import SubmitFinalizedReportDialog from './dialogs/SubmitFinalizedReportDialog'
import { useDialog } from '@/shared/hooks/useDialog'
import toast from 'react-hot-toast'
import useI18n from '@/shared/hooks/useI18n'
import { useCallback, useMemo, useState } from 'react'
import UpdateFinalizedReportDialog from './dialogs/UpdateFinalizedReportDialog'
import { Worker, Viewer } from '@react-pdf-viewer/core'
import '@react-pdf-viewer/core/lib/styles/index.css'
import { getPdfWorkerUrl } from '@/shared/utils/pdf'
import FinalizedReportPreviewEmbedded from './components/FinalizedReportPreviewEmbedded'
import { useInstallations } from '@/features/installations/hooks/useInstallations'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { languages } from '@/shared/components/LanguageSelector'

function FinalizedReportPdfPreview({
  finalizedReportId,
  organizationId,
  installationId,
  lang
}: {
  finalizedReportId: string
  organizationId: string
  installationId: string
  lang?: string
}) {
  const {
    data: submittedFinalizedReportDownloadUrl,
    isPending: isGettingSubmittedFinalizedReportDownloadUrl
  } = useGetCompletedFinalizedReportDownloadUrl(
    organizationId,
    installationId,
    finalizedReportId,
    lang
  )

  if (isGettingSubmittedFinalizedReportDownloadUrl) {
    return <Spinner className="size-5" />
  }

  return (
    <div className="px-52">
      <Worker workerUrl={getPdfWorkerUrl()}>
        <Viewer
          pageLayout={{
            transformSize: ({ size }) => ({
              height: size.height + 16,
              width: size.width + 16
            }),
            buildPageStyles: () => ({
              backgroundColor: 'transparent'
            })
          }}
          fileUrl={submittedFinalizedReportDownloadUrl!}
        />
      </Worker>
    </div>
  )
}

export default function FinalizedReportPage() {
  const installationId = useParams<{ installationId: string }>().installationId!
  const productionRouteId = useParams<{ productionRouteId: string }>().productionRouteId!
  const finalizedReportId = useParams<{ finalizedReportId: string }>().finalizedReportId!

  const [selectedPdfLanguage, setSelectedPdfLanguage] = useState<string | undefined>()
  const selectedPdfLanguageOption =
    languages.find(lang => lang.value === selectedPdfLanguage) || languages[0]
  const handleChangePdfLanguage = (lang: string) => {
    setSelectedPdfLanguage(lang)
  }

  const { t, dateFormatter, dateLongFormatterMonthYear } = useI18n()
  const organization = useSession().session.organization
  const organizationId = organization.id
  const installationsQuery = useInstallations()
  const installations = installationsQuery.data || []

  const installation = installations.find(installation => installation.id === installationId)
  const reportingPeriodBasis = installation?.basisForReportingPeriod

  const { data: finalizedReport, isPending: isGettingFinalizedReport } =
    useGetFinalizedReportPreview(organizationId, installationId, finalizedReportId)

  const { mutateAsync: downloadPreview, isPending: isDownloadingPreview } =
    useDownloadFinalizedReportPreview(organizationId, installationId, finalizedReportId)

  const { mutateAsync: downloadCompletedReport, isPending: isDownloadingCompletedReport } =
    useDownloadCompletedFinalizedReport(organizationId, installationId, finalizedReportId)

  const {
    open: isSubmitFinalizedReportDialogOpen,
    openDialog: openSubmitFinalizedReportDialog,
    closeDialog: closeSubmitFinalizedReportDialog
  } = useDialog()

  const {
    open: isEditFinalizedReportDialogOpen,
    openDialog: openEditFinalizedReportDialog,
    closeDialog: closeEditFinalizedReportDialog
  } = useDialog()

  const periodId = useMemo(() => {
    if (!finalizedReport) {return ''}
    return generatePeriodId(new Date(finalizedReport.startDate), new Date(finalizedReport.endDate))
  }, [finalizedReport])

  const formattedReportingPeriodForFileName = useMemo(() => {
    if (!finalizedReport) {return ''}
    return `${dateFormatter(new Date(finalizedReport.startDate))}-${dateFormatter(
      new Date(finalizedReport.endDate)
    )}`
  }, [finalizedReport, dateFormatter])

  const formattedReportingPeriod = useMemo(() => {
    if (!finalizedReport) {return ''}
    if (reportingPeriodBasis === 'CALENDAR_YEAR') {
      return `${new Date(finalizedReport.startDate).getFullYear()}`
    }
    return `${dateLongFormatterMonthYear(new Date(finalizedReport.startDate))} - ${dateLongFormatterMonthYear(
      new Date(finalizedReport.endDate)
    )}`
  }, [finalizedReport, dateLongFormatterMonthYear, reportingPeriodBasis])

  const productionRouteName = useMemo(() => {
    return finalizedReport?.productionRoute.name || ''
  }, [finalizedReport])

  const isEditable = finalizedReport?.status === 'PENDING'
  const isCompleted = finalizedReport?.status === 'SUBMITTED'

  const onDownloadPreviewClick = useCallback(async () => {
    try {
      const fileName = `${productionRouteName}-${formattedReportingPeriod}.pdf`.replace(/\s/g, '_')
      await downloadPreview({ filename: fileName, lang: selectedPdfLanguage })
    } catch (err) {
      console.error(err)
      toast.error(t('reports:finalizedReports.errors.downloadFailed'), {
        position: 'top-center'
      })
    }
  }, [downloadPreview, productionRouteName, formattedReportingPeriod, t, selectedPdfLanguage])

  const onDownloadCompletedReportClick = useCallback(async () => {
    try {
      await downloadCompletedReport(selectedPdfLanguage)
    } catch (err) {
      console.error(err)
      toast.error(t('reports:finalizedReports.errors.downloadFailed'), {
        position: 'top-center'
      })
    }
  }, [downloadCompletedReport, selectedPdfLanguage, t])

  const breadcrumbs = [
    { label: t('common:breadcrumbs.dashboard'), to: '/dashboard' },
    {
      label: `${productionRouteName} - ${formattedReportingPeriodForFileName}`,
      to: `/dashboard/${installationId}/production-routes/${productionRouteId}/reports/${periodId}`
    },
    { label: t('common:breadcrumbs.finalizedReport'), to: '' }
  ]

  const productionMethodCodes = useMemo(
    () => finalizedReport?.productionRoute.productionMethods.map(method => method.code) || [],
    [finalizedReport]
  )

  const handleOpenSubmitFinalizedReportDialog = useCallback(() => {
    openSubmitFinalizedReportDialog()
  }, [openSubmitFinalizedReportDialog])

  if (isGettingFinalizedReport) {
    return <LoadingScreen />
  }

  if (!finalizedReport) {
    return <Navigate to="../finalized" />
  }

  return (
    <>
      <div>
        <BreadcrumbsBar links={breadcrumbs} />
        <div className="relative px-6 py-5 space-y-8">
          <header className="flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold">{productionRouteName}</h2>
              <p className="text-text-secondary text-sm">{formattedReportingPeriod}</p>
            </div>
            <div className="flex items-center gap-4">
              {finalizedReport?.finalizedAt && (
                <div className="flex items-center gap-1">
                  <CheckCircleIcon className="w-5 text-green-600" />
                  <span className="text-text-secondary">
                    {t('reports:finalizedReports.finalizedAt', {
                      date: dateFormatter(new Date(finalizedReport.finalizedAt))
                    })}
                  </span>
                </div>
              )}
              <Popover>
                <PopoverButton>
                  <Button variant="subtle" sizeType="sm" className="flex items-center gap-2">
                    {selectedPdfLanguage ? (
                      <>
                        <span className="text-sm font-medium">
                          {selectedPdfLanguageOption.flag}
                        </span>
                        <span className="text-sm font-medium">
                          {selectedPdfLanguageOption.label}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm font-medium">
                        {t('finalizedReportDocument:header.selectReportLanguage')}
                      </span>
                    )}
                    <ChevronDownIcon className="w-3" />
                  </Button>
                </PopoverButton>
                <PopoverPanel
                  transition
                  anchor="bottom end"
                  className="z-40 min-w-32  transition shadow-dropdown border p-4 border-border-main rounded bg-white duration-200 ease-in-out [--anchor-gap:8px] data-[closed]:-translate-y-1 data-[closed]:opacity-0"
                >
                  {({ close }) => (
                    <div className="flex flex-col gap-2">
                      {languages.map(lang => (
                        <button
                          className="text-left text-sm font-medium"
                          key={lang.value}
                          onClick={() => {
                            handleChangePdfLanguage(lang.value)
                            close()
                          }}
                        >
                          {lang.flag} {lang.label} {lang.value === selectedPdfLanguage && '✓'}
                        </button>
                      ))}
                    </div>
                  )}
                </PopoverPanel>
              </Popover>
              <Button
                variant="subtle"
                sizeType="sm"
                icon={<DownloadArrowDownIcon />}
                onClick={isEditable ? onDownloadPreviewClick : onDownloadCompletedReportClick}
                disabled={isDownloadingPreview || isDownloadingCompletedReport}
                isLoading={isDownloadingPreview || isDownloadingCompletedReport}
              >
                {isEditable ? t('common:actions.preview') : t('common:actions.download')}
              </Button>
              {isEditable && (
                <>
                  <Button
                    variant="subtle"
                    sizeType="sm"
                    icon={<PencilIcon />}
                    onClick={openEditFinalizedReportDialog}
                  >
                    {t('common:actions.edit')}
                  </Button>
                  <Button
                    sizeType="sm"
                    icon={<CheckCircleIcon />}
                    onClick={handleOpenSubmitFinalizedReportDialog}
                  >
                    {t('common:actions.finalize')}
                  </Button>
                </>
              )}
            </div>
          </header>
          {isCompleted && (
            <FinalizedReportPdfPreview
              finalizedReportId={finalizedReportId}
              organizationId={organizationId}
              installationId={installationId}
              lang={selectedPdfLanguage}
            />
          )}
          {!isCompleted && (
            <FinalizedReportPreviewEmbedded
              data={finalizedReport!}
              organization={organization}
              isPreview
              selectedPdfLanguage={selectedPdfLanguage}
            />
          )}
        </div>
      </div>
      <SubmitFinalizedReportDialog
        isOpen={isSubmitFinalizedReportDialogOpen}
        close={closeSubmitFinalizedReportDialog}
        installationId={installationId}
        finalizedReportId={finalizedReportId}
        selectedPdfLanguage={selectedPdfLanguage}
      />
      <UpdateFinalizedReportDialog
        isOpen={isEditFinalizedReportDialogOpen}
        close={closeEditFinalizedReportDialog}
        installationId={installationId}
        initialState={finalizedReport}
        finalizedReportId={finalizedReportId}
        productionMethodCodes={productionMethodCodes}
      />
    </>
  )
}
